import React from "react";
import { useMappedState } from "redux-react-hook";
import { useHistory } from "react-router-dom";

import {
  IOrder,
  ListOrdersByProcessJobFilterParams,
  ListOrdersByProcessJobParams,
  OrderAuditLogChangeType,
  ordersApi
} from "src/core/orders/api/orders.api";
import { OrderActionsList } from "src/core/orders/components/OrderActionsList";
import { adminOrderTableColumns } from "src/core/shared/components/tables";
import useTerminology from "src/core/shared/hooks/useTerminology";
import { IStore } from "src/core/shared/store";
import { Table } from "src/library/components/Tables";
import { ITableInstance } from "src/library/components/Tables/Table";
import { Text } from "src/library/components/Text";
import {
  PaginatedData,
  PaginatedParams
} from "src/library/utils";

const defaultPagination: PaginatedParams<IOrder> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "orderDeliveryDate", "DESC" ] ]
};

const RelatedOrdersTab: React.ReactFCWithChildren<{processJobId: number}> = ({ processJobId }) => {
  const terminology = useTerminology();
  const history = useHistory();

  const {
    orderStatuses, role, erp, installedModules
  } = useMappedState((store: IStore) => ({
    orderStatuses: store.global.settings ? store.global.settings.orderStatuses : [],
    role: store.global.role,
    erp: store.global.settings && store.global.settings.erp.name,
    installedModules: store.global.settings?.installedModules || []
  }));

  const [ getProcessJobOrdersResponse, getProcessJobOrdersRequest ] = ordersApi<PaginatedData<IOrder>>("LIST_PROCESS_JOB_ORDERS_BY_AUDIT_LOG");
  const TableRef = React.useRef<ITableInstance<IOrder>>(null);

  const reloadData = React.useCallback(() => {
    if (TableRef.current) {
      TableRef.current.reloadTable();
    }
  }, [ TableRef ]);

  const onFetchOrdersData = React.useCallback(
    (filters: ListOrdersByProcessJobFilterParams, pagination: PaginatedParams<IOrder>) => {
      if (processJobId) {
        filters = {
          auditLogType: OrderAuditLogChangeType.ProductReplacement,
          ...filters
        };

        getProcessJobOrdersRequest<ListOrdersByProcessJobParams>({
          processJobId,
          filters,
          pagination
        });
      }
    },
    [ processJobId ]
  );

  if (!terminology) {
    return null;
  }

  return (
    <>
      <Text as="p">
        Showing all orders affected by this job
      </Text>

      <Table
        tableId={`job-related-orders-${processJobId}`}
        tableOf={terminology.order.plural.text}
        totalRows={getProcessJobOrdersResponse.data ? getProcessJobOrdersResponse.data.count : 0}
        pages={getProcessJobOrdersResponse.data ? getProcessJobOrdersResponse.data.pages : 0}
        loading={getProcessJobOrdersResponse.pending}
        data={getProcessJobOrdersResponse.data ? getProcessJobOrdersResponse.data.result : []}
        ref={TableRef}
        columns={adminOrderTableColumns({
          installedModules,
          terminology,
          orderStatuses,
          ActionButton: row => {
            if (erp && role) {
              return (
                <OrderActionsList
                  erp={erp}
                  terminology={terminology}
                  role={role}
                  order={row.original}
                  reload={reloadData}
                />
              );
            }

            return null;
          }
        })}
        onFetchData={onFetchOrdersData}
        defaultPagination={defaultPagination}
        error={getProcessJobOrdersResponse.error}
        mainAction={row => history.push(`/orders/${row.original.id}`)}
      />
    </>
  );
};

export default RelatedOrdersTab;